import { Check, Eye, EyeOff, X } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../../lib/utils';

// ─── Strength helpers ─────────────────────────────────────────────────────────

interface Requirement {
  label: string;
  test: (v: string) => boolean;
}

const REQUIREMENTS: Requirement[] = [
  { label: 'At least 12 characters', test: (v) => v.length >= 12 },
  { label: 'At least 1 lowercase letter', test: (v) => /[a-z]/.test(v) },
  { label: 'At least 1 uppercase letter', test: (v) => /[A-Z]/.test(v) },
  { label: 'At least 1 number', test: (v) => /\d/.test(v) },
  { label: 'At least 1 special character', test: (v) => /[^a-zA-Z0-9]/.test(v) },
];

function barColor(score: number, idx: number): string {
  if (idx >= score) return 'bg-muted';
  if (score <= 2) return 'bg-destructive';
  if (score === 3) return 'bg-warning';
  if (score === 4) return 'bg-info';
  return 'bg-success';
}

// ─── Size maps ────────────────────────────────────────────────────────────────

const sizeClasses = {
  sm: 'h-7 px-2 py-1 text-base sm:text-xs',
  md: 'h-9 px-3 py-2 text-base sm:text-sm',
  lg: 'h-10 px-4 py-2 text-base',
} as const;

const endPad = { sm: 'pr-8', md: 'pr-10', lg: 'pr-12' } as const;
const slotR = { sm: 'right-2', md: 'right-3', lg: 'right-3.5' } as const;
const iconSize = { sm: 'size-3.5', md: 'size-4', lg: 'size-5' } as const;

const variantClasses = {
  outline: 'border border-input bg-input-bg shadow-sm',
  filled: 'border border-transparent bg-muted',
  ghost: 'border border-transparent bg-transparent',
} as const;

// ─── Props ────────────────────────────────────────────────────────────────────

export interface InputPasswordProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> {
  size?: 'sm' | 'md' | 'lg';
  variant?: 'outline' | 'filled' | 'ghost';
  /** Show the strength meter and requirements checklist below the input. */
  showStrength?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

const InputPassword = React.forwardRef<HTMLInputElement, InputPasswordProps>(
  (
    { size = 'md', variant = 'outline', showStrength = true, className, onChange, ...props },
    ref,
  ) => {
    const [show, setShow] = React.useState(false);
    const [value, setValue] = React.useState('');

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(e.target.value);
      onChange?.(e);
    };

    const results = REQUIREMENTS.map((r) => r.test(value));
    const score = results.filter(Boolean).length;

    return (
      <div className={cn('w-full space-y-3', className)}>
        {/* ── Input ──────────────────────────────────────────────────────────── */}
        <div className="relative w-full">
          <input
            ref={ref}
            type={show ? 'text' : 'password'}
            value={value}
            onChange={handleChange}
            className={cn(
              'w-full rounded-[var(--field-radius,var(--radius))] text-foreground',
              'placeholder:text-muted-foreground',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              'focus-visible:ring-offset-2 focus-visible:ring-offset-background',
              'disabled:cursor-not-allowed disabled:opacity-50',
              'aria-[invalid=true]:border-destructive aria-[invalid=true]:focus-visible:ring-destructive',
              variantClasses[variant],
              sizeClasses[size],
              endPad[size],
            )}
            {...props}
          />
          <button
            type="button"
            tabIndex={-1}
            onClick={() => setShow((s) => !s)}
            aria-label={show ? 'Hide password' : 'Show password'}
            className={cn(
              'absolute top-1/2 -translate-y-1/2 cursor-pointer text-muted-foreground',
              'hover:text-foreground focus-visible:outline-none',
              slotR[size],
            )}
          >
            {show ? (
              <EyeOff className={iconSize[size]} aria-hidden="true" />
            ) : (
              <Eye className={iconSize[size]} aria-hidden="true" />
            )}
          </button>
        </div>

        {showStrength && (
          <>
            {/* ── Strength bars ──────────────────────────────────────────────── */}
            {/* biome-ignore lint/a11y/useSemanticElements: native <meter> cannot render custom child segments */}
            <div
              className="flex gap-1.5"
              role="meter"
              aria-label="Password strength"
              aria-valuenow={score}
              aria-valuemin={0}
              aria-valuemax={5}
            >
              {Array.from({ length: 5 }, (_, i) => (
                <div
                  key={i}
                  className={cn(
                    'h-1 flex-1 rounded-full transition-colors duration-300',
                    barColor(score, i),
                  )}
                />
              ))}
            </div>

            {/* ── Requirements ───────────────────────────────────────────────── */}
            <div className="space-y-2">
              <p className="text-sm font-medium text-foreground">Enter a password. Must contain:</p>
              <ul className="space-y-1.5">
                {REQUIREMENTS.map((req, i) => (
                  <li key={req.label} className="flex items-center gap-2 text-sm">
                    {results[i] ? (
                      <Check className="size-4 shrink-0 text-success" aria-hidden="true" />
                    ) : (
                      <X className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                    )}
                    <span className={results[i] ? 'text-foreground' : 'text-muted-foreground'}>
                      {req.label}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
      </div>
    );
  },
);
InputPassword.displayName = 'InputPassword';

// ─── Exports ──────────────────────────────────────────────────────────────────

export { InputPassword };
